import React, { useState, useRef, useEffect } from 'react';
import {
  Box, Typography, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, FormControl, InputLabel, Select, MenuItem,
  ListSubheader, TextField, CircularProgress, IconButton, Tooltip, Checkbox,
} from '@mui/material';
import UploadFileIcon          from '@mui/icons-material/UploadFile';
import LinkIcon                from '@mui/icons-material/Link';
import AddIcon                 from '@mui/icons-material/Add';
import DeleteOutlineIcon       from '@mui/icons-material/DeleteOutline';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import OpenInNewIcon           from '@mui/icons-material/OpenInNew';
import SwapHorizIcon           from '@mui/icons-material/SwapHoriz';
import { useInventory } from '../../contexts/InventoryContext';
import MediaThumbnail from './MediaThumbnail';

// ─── Asset type groups — ordered by daily usage frequency ──────────────────
// Only keep Marketing & Sales and Media; hide other categories from UI.
const ASSET_TYPE_GROUPS = [
  {
    group: 'Marketing & Sales',
    options: [
      { value: 'brochure',     label: 'Brochure' },
      { value: 'floor_plan',   label: 'Floor Plan' },
      { value: 'site_plan',    label: 'Site Plan' },
      { value: 'master_plan',  label: 'Master Plan' },
      { value: 'price_list',   label: 'Price List' },
      { value: 'payment_plan', label: 'Payment Plan' },
      { value: 'cost_sheet',   label: 'Cost Sheet' },
    ],
  },
  {
    group: 'Media',
    options: [
      { value: 'image',         label: 'Image' },
      { value: 'video',         label: 'Video' },
      // { value: 'virtual_tour',  label: 'Virtual Tour' },
      // { value: 'drone_footage', label: 'Drone Footage' },
    ],
  },
  // {
  //   group: 'Legal & Compliance',
  //   options: [
  //     { value: 'rera_document',           label: 'RERA Document' },
  //     { value: 'sale_deed',               label: 'Sale Deed' },
  //     { value: 'title_deed',              label: 'Title Deed' },
  //     { value: 'encumbrance_certificate', label: 'Encumbrance Certificate' },
  //     { value: 'occupancy_certificate',   label: 'Occupancy Certificate' },
  //     { value: 'completion_certificate',  label: 'Completion Certificate' },
  //     { value: 'noc',                     label: 'NOC' },
  //   ],
  // },
  // {
  //   group: 'Data & Sheets',
  //   options: [
  //     { value: 'inventory_sheet',    label: 'Inventory Sheet' },
  //     { value: 'booking_form',       label: 'Booking Form' },
  //     { value: 'agreement_for_sale', label: 'Agreement for Sale' },
  //     { value: 'allotment_letter',   label: 'Allotment Letter' },
  //   ],
  // },
  // {
  //   group: 'Other',
  //   options: [{ value: 'other', label: 'Other' }],
  // },
];

const DEFAULT_TYPE        = '';
const ADD_NEW_VALUE       = '__add_new__';
const ALL_OPTIONS         = ASSET_TYPE_GROUPS.flatMap((g) => g.options);
const MAX_UPLOAD_FILES    = 10;
const SINGLE_FILE_TYPES   = [
  'brochure',
  'price_list',
  'floor_plan',
  'site_plan',
  'master_plan',
  'payment_plan',
  'cost_sheet',
];

// File size and count limits for specific asset types
const MAX_IMAGE_SIZE_MB = 5;
const MAX_VIDEO_SIZE_MB = 20;
const MAX_IMAGE_COUNT = 6;
const MAX_VIDEO_COUNT = 2;
const MAX_IMAGE_SIZE_BYTES = MAX_IMAGE_SIZE_MB * 1024 * 1024;
const MAX_VIDEO_SIZE_BYTES = MAX_VIDEO_SIZE_MB * 1024 * 1024;

const ASSET_TYPE_TO_GROUP = (() => {
  const m = {};
  ASSET_TYPE_GROUPS.forEach(({ group, options }) => {
    options.forEach(({ value }) => { m[value] = group; });
  });
  return m;
})();

function getGroupForType(assetType) {
  return ASSET_TYPE_TO_GROUP[assetType] || 'Other';
}

const ASSET_TYPE_ORDER = ALL_OPTIONS.map((o) => o.value);

function getLabelForType(assetType) {
  return ALL_OPTIONS.find((o) => o.value === assetType)?.label || (assetType || 'Other').replace(/_/g, ' ');
}

function sortGroupsByAssetType(groups) {
  return [...groups].sort((a, b) => {
    const i = ASSET_TYPE_ORDER.indexOf(a.asset_type);
    const j = ASSET_TYPE_ORDER.indexOf(b.asset_type);
    if (i === -1 && j === -1) return (a.asset_type || '').localeCompare(b.asset_type || '');
    if (i === -1) return 1;
    if (j === -1) return -1;
    return i - j;
  });
}

const T = {
  ink: '#0f1f15', ink2: '#1e3a28', muted: '#506459', subtle: '#7a9585',
  mint: '#00c46d', mintSoft: 'rgba(0,196,109,.08)', mintBorder: 'rgba(0,196,109,.25)',
  border: '#e0ece4', border2: '#c8ddd2',
  surface: '#f4f8f5', surface2: '#ebf4ee', white: '#ffffff',
  g50: '#eef7f1', g600: '#246040',
};

function renderGroupedOptions(disabledTypes = [], groups = []) {
  // Calculate current counts for showing in disabled message
  const currentImageCount = groups.find((g) => g.asset_type === 'image')?.assets?.length || 0;
  const currentVideoCount = groups.find((g) => g.asset_type === 'video')?.assets?.length || 0;
  
  const getDisabledReason = (value) => {
    if (value === 'image' && currentImageCount >= MAX_IMAGE_COUNT) {
      return `(max ${MAX_IMAGE_COUNT} reached)`;
    }
    if (value === 'video' && currentVideoCount >= MAX_VIDEO_COUNT) {
      return `(max ${MAX_VIDEO_COUNT} reached)`;
    }
    return '(already exists)';
  };
  
  const items = [];
  ASSET_TYPE_GROUPS.forEach(({ group, options }) => {
    items.push(
      <ListSubheader key={'grp-' + group} sx={{
        fontSize: '10px', fontWeight: 700, letterSpacing: '0.6px',
        textTransform: 'uppercase', color: T.subtle,
        lineHeight: '28px', background: T.surface,
      }}>
        {group}
      </ListSubheader>
    );
    options.forEach(({ value, label }) =>
      items.push(
        <MenuItem 
          key={value} 
          value={value} 
          disabled={disabledTypes.includes(value)}
          sx={{ 
            pl: 2.5, 
            fontSize: '13px', 
            color: disabledTypes.includes(value) ? T.subtle : T.ink,
            opacity: disabledTypes.includes(value) ? 0.5 : 1,
          }}
        >
          {label} {disabledTypes.includes(value) && getDisabledReason(value)}
        </MenuItem>
      )
    );
  });
  items.push(
    <MenuItem key={ADD_NEW_VALUE} value={ADD_NEW_VALUE} sx={{
      fontStyle: 'italic', color: T.mint, fontSize: '13px',
      borderTop: '1px solid ' + T.border, mt: 0.5,
    }}>
      + Add new label
    </MenuItem>
  );
  return items;
}

function AssetTypeSelect({ value, onChange, disabledTypes = [], groups = [] }) {
  return (
    <FormControl size="small" fullWidth>
      <InputLabel sx={{ fontSize: '13px' }}>Select file type</InputLabel>
      <Select
        value={value || ''}
        label="Select file type"
        onChange={(e) => onChange(e.target.value)}
        renderValue={(v) => {
          if (v === ADD_NEW_VALUE) return <em>Custom label</em>;
          return (ALL_OPTIONS.find((o) => o.value === v) || {}).label || v;
        }}
        MenuProps={{ PaperProps: { sx: { maxHeight: 380, background: T.white } } }}
        sx={{ fontSize: '13px' }}
      >
        {renderGroupedOptions(disabledTypes, groups)}
      </Select>
    </FormControl>
  );
}

function fileColor(assetType) {
  if (['image'].includes(assetType)) return '#0ea5e9';
  if (['video', 'virtual_tour', 'drone_footage'].includes(assetType)) return '#8b5cf6';
  if (['brochure', 'floor_plan', 'site_plan', 'master_plan'].includes(assetType)) return '#f59e0b';
  if (['rera_document', 'sale_deed', 'title_deed', 'noc', 'occupancy_certificate',
       'completion_certificate', 'encumbrance_certificate'].includes(assetType)) return '#ef4444';
  return T.mint;
}

/** Sanitize for use as filename: remove/replace invalid chars, ensure .pdf for brochure */
/** Replaces spaces with underscores so filenames never contain spaces. */
function filenameSafeName(name) {
  return (name || '').trim().replace(/\s+/g, '_');
}

function sanitizeFilenameForBrochure(projectName, file) {
  if (!projectName || typeof projectName !== 'string') return null;
  const base = projectName.replace(/[/\\:*?"<>|,]/g, '_').replace(/\s+/g, '_').trim();
  if (!base) return null;
  const ext = (file && file.name && file.name.includes('.')) ? file.name.split('.').pop().toLowerCase() : 'pdf';
  return `${base}.${ext}`;
}

function MediaCard({ item, selected, onToggleSelect, onDelete, onReplace }) {
  const color = fileColor(item.asset_type);
  const typeLabel = (ALL_OPTIONS.find((o) => o.value === item.asset_type) || {}).label || item.asset_type;
  const thumbnailUrl = item.url || (item.urls && item.urls[0]);
  const mediaUrl = thumbnailUrl ? toAbsoluteMediaUrl(thumbnailUrl) : null;
  const isBrochure = (item.asset_type || '').toLowerCase() === 'brochure';
  const showReplace = isBrochure && Boolean(onReplace);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        aspectRatio: '1',
        borderRadius: '10px',
        overflow: 'hidden',
        background: T.white,
        border: '2px solid ' + (selected ? T.mint : T.border),
        transition: 'border-color 150ms, box-shadow 150ms',
        '&:hover': { borderColor: selected ? T.mint : T.border2, boxShadow: '0 2px 8px rgba(15,31,21,.06)' },
        '&:hover .media-card-actions': { opacity: 1 },
        '&:hover .media-card-preview-overlay': { opacity: 1 },
      }}
    >
      <Box
        sx={{ width: '100%', aspectRatio: '1', flexShrink: 0, overflow: 'hidden', position: 'relative', cursor: 'pointer' }}
        onClick={() => onToggleSelect(item.id)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggleSelect(item.id); } }}
      >
        <Box
          sx={{ position: 'absolute', top: 6, left: 6, zIndex: 3 }}
          onClick={(e) => e.stopPropagation()}
        >
          <Checkbox
            size="small"
            checked={selected}
            onChange={() => onToggleSelect(item.id)}
            onClick={(e) => e.stopPropagation()}
            sx={{ color: T.white, bgcolor: 'rgba(0,0,0,.35)', borderRadius: 1, p: 0.25, '&.Mui-checked': { color: T.mint } }}
          />
        </Box>
        <MediaThumbnail
          url={thumbnailUrl}
          assetType={item.asset_type}
          size={200}
          borderRadius={0}
          fallbackColor={color}
          sx={{ width: '100%', height: '100%', borderRadius: 0 }}
        />
        {thumbnailUrl && (
          <Box
            className="media-card-preview-overlay"
            onClick={(e) => e.stopPropagation()}
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(15,31,21,0.45)',
              opacity: 0,
              transition: 'opacity 180ms ease',
              zIndex: 2,
            }}
          >
            <Tooltip title="Open asset URL in new tab">
              <Button
                size="small"
                variant="contained"
                component="a"
                href={mediaUrl}
                target="_blank"
                rel="noopener noreferrer"
                startIcon={<OpenInNewIcon sx={{ fontSize: 16 }} />}
                sx={{
                  textTransform: 'none',
                  fontSize: '12px',
                  fontWeight: 600,
                  background: T.white,
                  color: T.ink2,
                  boxShadow: '0 2px 8px rgba(0,0,0,.2)',
                  '&:hover': { background: T.g50, color: T.g600 },
                }}
              >
                Open in new tab
              </Button>
            </Tooltip>
          </Box>
        )}
      </Box>
      <Box sx={{ p: '8px 10px', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: 0.5 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{
            fontSize: '11.5px', fontWeight: 600, color: T.ink, lineHeight: 1.3,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {item.asset_name || typeLabel}
          </Typography>
          <Typography sx={{ fontSize: '10px', color: T.subtle }}>
            {typeLabel}
            {item.urls && item.urls.length > 1 && (
              <Box component="span" sx={{
                ml: '6px', px: '5px', py: '1px', borderRadius: '4px',
                background: T.surface, border: '1px solid ' + T.border,
                fontSize: '10px', fontWeight: 600, color: T.muted,
              }}>
                {item.urls.length} links
              </Box>
            )}
          </Typography>
        </Box>
        <Box
          className="media-card-actions"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0,
            mt: '2px',
            opacity: 0,
            transition: 'opacity 150ms ease',
          }}
        >
          {showReplace && (
            <Tooltip title="Replace brochure">
              <IconButton size="small" onClick={() => onReplace(item)}
                sx={{ color: T.subtle, '&:hover': { color: T.mint }, p: 0.25 }}>
                <SwapHorizIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          )}
          {thumbnailUrl && (
            <Tooltip title="Open asset URL in new tab">
              <IconButton size="small" component="a" href={mediaUrl} target="_blank" rel="noopener noreferrer"
                sx={{ color: T.subtle, '&:hover': { color: T.mint }, p: 0.25 }}>
                <OpenInNewIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Remove">
            <IconButton size="small" onClick={() => onDelete(item.id)}
              sx={{ color: T.subtle, '&:hover': { color: '#ef4444' }, p: 0.25 }}>
              <DeleteOutlineIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
}

function UrlRow({ value, onChange, onRemove, index, showRemove }) {
  return (
    <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
      <TextField
        size="small" fullWidth
        label={index === 0 ? 'URL' : 'URL ' + (index + 1)}
        placeholder="https://..."
        value={value}
        onChange={(e) => onChange(e.target.value)}
        sx={{ '& .MuiInputBase-input': { fontSize: '13px' } }}
      />
      {showRemove && (
        <IconButton size="small" onClick={onRemove}
          sx={{ color: T.subtle, '&:hover': { color: '#ef4444' } }}>
          <DeleteOutlineIcon sx={{ fontSize: 16 }} />
        </IconButton>
      )}
    </Box>
  );
}

/** Ensure URL has a protocol so it opens in a new tab instead of as a React Router path */
function toAbsoluteMediaUrl(url) {
  if (!url || typeof url !== 'string') return url;
  const u = url.trim();
  if (/^https?:\/\//i.test(u)) return u;
  return 'https://' + u;
}

const mapAssetFromApi = (a, groupName) => ({
  ...a,
  asset_name: a.name,
  group_name: a.group_name || groupName,
  urls: a.url ? [a.url] : [],
});

const MediaAndDocuments = ({ projectId, configurationId, unitId, config, unit, projectName, openUploadWithType, onOpenUploadWithTypeConsumed, onBrochureChange }) => {
  const { getMedia, uploadMedia, addMediaLinks, deleteMedia, deleteMediaBulk } = useInventory();

  const asset_model  = unitId ? 'unit' : configurationId ? 'configuration' : 'project';
  const reference_id = unitId ?? configurationId ?? projectId;
  const isUnit          = Boolean(unitId);
  const isConfiguration = Boolean(configurationId) && !isUnit;
  const isProjectLevel  = Boolean(projectId) && !configurationId && !unitId;

  const [groups, setGroups]             = useState([]);
  const [mediaLoading, setMediaLoading] = useState(false);
  const [selectedIds, setSelectedIds]   = useState(() => new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [replacingBrochureId, setReplacingBrochureId] = useState(null);

  useEffect(() => {
    if (!reference_id || !getMedia) return;
    setMediaLoading(true);
    getMedia(asset_model, reference_id)
      .then((data) => {
        const raw = (data && data.groups) || [];
        setGroups(
          raw.map((g) => ({
            asset_type: g.asset_type || 'other',
            assets: (g.assets || []).map((a) => mapAssetFromApi(a, g.asset_type)),
          }))
        );
      })
      .catch(() => setGroups([]))
      .finally(() => setMediaLoading(false));
  }, [asset_model, reference_id, getMedia]);

  // Open upload dialog with preset type (e.g. from "Upload Brochure" in project header)
  useEffect(() => {
    if (!openUploadWithType || !reference_id) return;
    setOpenUpload(true);
    setUpType(openUploadWithType);
    if ((openUploadWithType || '').toLowerCase() === 'brochure' && projectName) setUpName(filenameSafeName(projectName));
    onOpenUploadWithTypeConsumed?.();
  }, [openUploadWithType, reference_id, onOpenUploadWithTypeConsumed, projectName]);

  // Upload dialog state
  const [openUpload, setOpenUpload] = useState(false);
  const [upType, setUpType]         = useState(DEFAULT_TYPE);
  const [upCustom, setUpCustom]     = useState('');
  const [upName, setUpName]         = useState('');
  const [upFiles, setUpFiles]      = useState([]);
  const [dragOver, setDragOver]     = useState(false);
  const [uploading, setUploading]  = useState(false);
  const inputRef = useRef(null);

  const isUpCustom   = upType === ADD_NEW_VALUE;
  const upEffective  = isUpCustom ? upCustom.trim() : upType;
  const lowerType    = (upEffective || '').toLowerCase();
  const isBrochureUpload   = lowerType === 'brochure';
  const isImageUpload = lowerType === 'image';
  const isVideoUpload = lowerType === 'video';
  const isSingleFileUpload = SINGLE_FILE_TYPES.includes(lowerType);
  
  // Get current count of images/videos for this entity
  const currentImageCount = groups.find((g) => g.asset_type === 'image')?.assets?.length || 0;
  const currentVideoCount = groups.find((g) => g.asset_type === 'video')?.assets?.length || 0;
  
  // Calculate remaining slots
  const remainingImageSlots = MAX_IMAGE_COUNT - currentImageCount;
  const remainingVideoSlots = MAX_VIDEO_COUNT - currentVideoCount;
  
  // Check if we can add more files based on type
  let canAddMore = false;
  if (isSingleFileUpload) {
    canAddMore = upFiles.length === 0;
  } else if (isImageUpload) {
    canAddMore = upFiles.length < remainingImageSlots;
  } else if (isVideoUpload) {
    canAddMore = upFiles.length < remainingVideoSlots;
  } else {
    canAddMore = upFiles.length < MAX_UPLOAD_FILES;
  }
  
  const canUpload = Boolean(upEffective && upFiles.length > 0);

  // When user selects Brochure at project level, pre-fill name with project name
  useEffect(() => {
    if (openUpload && (upEffective || '').toLowerCase() === 'brochure' && isProjectLevel && projectName && !upName) {
      setUpName(filenameSafeName(projectName));
    }
  }, [openUpload, upEffective, isProjectLevel, projectName]);

  const resetUpload = () => {
    setOpenUpload(false); setUpType(DEFAULT_TYPE); setUpCustom('');
    setUpName(''); setUpFiles([]); setDragOver(false); setUploading(false);
    setReplacingBrochureId(null);
  };

  const addFiles = (fileList) => {
    if (!fileList || !fileList.length) return;
    const filesArray = Array.from(fileList);

    if (isBrochureUpload) {
      const pdfFiles = filesArray.filter((file) => file.type === 'application/pdf');
      if (pdfFiles.length === 0) {
        alert('Brochure uploads only accept PDF files');
        return;
      }
      setUpFiles([pdfFiles[0]]);
      return;
    }

    if (isImageUpload) {
      // Validate image files
      const imageFiles = filesArray.filter((file) => file.type.startsWith('image/'));
      if (imageFiles.length === 0) {
        alert('Please select image files only');
        return;
      }
      
      // Check file sizes
      const oversizedFiles = imageFiles.filter((file) => file.size > MAX_IMAGE_SIZE_BYTES);
      if (oversizedFiles.length > 0) {
        alert(`Image files must be ${MAX_IMAGE_SIZE_MB}MB or smaller. These files are too large:\n${oversizedFiles.map(f => `${f.name} (${(f.size / 1024 / 1024).toFixed(2)}MB)`).join('\n')}`);
        return;
      }
      
      // Check total count including already selected files
      const totalAfterAdd = upFiles.length + imageFiles.length;
      const availableSlots = remainingImageSlots - upFiles.length;
      
      if (totalAfterAdd > remainingImageSlots) {
        alert(`You can only upload ${remainingImageSlots} more image${remainingImageSlots !== 1 ? 's' : ''} (maximum ${MAX_IMAGE_COUNT} total). You have ${currentImageCount} image${currentImageCount !== 1 ? 's' : ''} already uploaded.`);
        // Take only what fits
        const filesToAdd = imageFiles.slice(0, availableSlots);
        if (filesToAdd.length > 0) {
          setUpFiles([...upFiles, ...filesToAdd]);
        }
        return;
      }
      
      setUpFiles([...upFiles, ...imageFiles]);
      return;
    }

    if (isVideoUpload) {
      // Validate video files
      const videoFiles = filesArray.filter((file) => file.type.startsWith('video/'));
      if (videoFiles.length === 0) {
        alert('Please select video files only');
        return;
      }
      
      // Check file sizes
      const oversizedFiles = videoFiles.filter((file) => file.size > MAX_VIDEO_SIZE_BYTES);
      if (oversizedFiles.length > 0) {
        alert(`Video files must be ${MAX_VIDEO_SIZE_MB}MB or smaller. These files are too large:\n${oversizedFiles.map(f => `${f.name} (${(f.size / 1024 / 1024).toFixed(2)}MB)`).join('\n')}`);
        return;
      }
      
      // Check total count including already selected files
      const totalAfterAdd = upFiles.length + videoFiles.length;
      const availableSlots = remainingVideoSlots - upFiles.length;
      
      if (totalAfterAdd > remainingVideoSlots) {
        alert(`You can only upload ${remainingVideoSlots} more video${remainingVideoSlots !== 1 ? 's' : ''} (maximum ${MAX_VIDEO_COUNT} total). You have ${currentVideoCount} video${currentVideoCount !== 1 ? 's' : ''} already uploaded.`);
        // Take only what fits
        const filesToAdd = videoFiles.slice(0, availableSlots);
        if (filesToAdd.length > 0) {
          setUpFiles([...upFiles, ...filesToAdd]);
        }
        return;
      }
      
      setUpFiles([...upFiles, ...videoFiles]);
      return;
    }

    if (isSingleFileUpload) {
      setUpFiles([filesArray[0]]);
      return;
    }

    const next = [...upFiles, ...filesArray].slice(0, MAX_UPLOAD_FILES);
    setUpFiles(next);
  };

  const removeFileAt = (index) => setUpFiles((prev) => prev.filter((_, i) => i !== index));

  const handleUpload = async () => {
    if (!canUpload) return;
    setUploading(true);
    const groupName = getGroupForType(upEffective);
    const isBrochureUpload = (upEffective || '').toLowerCase() === 'brochure';
    const rawName = isBrochureUpload && isProjectLevel && projectName
      ? (upName || projectName).trim() || projectName
      : upName;
    const effectiveName = filenameSafeName(rawName);
    try {
      if (replacingBrochureId) {
        await deleteMedia(replacingBrochureId);
        setGroups((prev) =>
          prev
            .map((g) => ({ ...g, assets: g.assets.filter((a) => a.id !== replacingBrochureId) }))
            .filter((g) => g.assets.length > 0)
        );
        setSelectedIds((prev) => { const n = new Set(prev); n.delete(replacingBrochureId); return n; });
        if (onBrochureChange) onBrochureChange();
      }
      const form = new FormData();
      form.append('asset_model', asset_model);
      form.append('reference_id', reference_id);
      form.append('group_name', groupName);
      form.append('asset_type', upEffective);
      if (effectiveName) form.append('name', effectiveName);
      upFiles.forEach((file) => {
        const fileToSend = isBrochureUpload && isProjectLevel && projectName
          ? (sanitizeFilenameForBrochure(projectName, file) && new File([file], sanitizeFilenameForBrochure(projectName, file), { type: file.type })) || file
          : file;
        form.append('files', fileToSend);
      });
      const data = await uploadMedia(form);
      const added = (Array.isArray(data) ? data : [data]).map((a) => mapAssetFromApi(a, upEffective));
      setGroups((prev) => {
        const next = prev.map((g) => (g.asset_type === upEffective ? { ...g, assets: [...added, ...g.assets] } : g));
        if (!next.some((g) => g.asset_type === upEffective)) next.push({ asset_type: upEffective, assets: added });
        return sortGroupsByAssetType(next);
      });
      if (isBrochureUpload) onBrochureChange?.();
      resetUpload();
    } catch (err) {
      console.error('Media upload failed:', err);
    } finally {
      setUploading(false);
    }
  };

  const handleDrop     = (e) => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer?.files); };
  const handleDragOver = (e) => { e.preventDefault(); if (canAddMore) setDragOver(true); };
  const handleDragLeave = () => setDragOver(false);

  // Link dialog state
  const [openLink, setOpenLink] = useState(false);
  const [lnType, setLnType]     = useState(DEFAULT_TYPE);
  const [lnCustom, setLnCustom] = useState('');
  const [lnName, setLnName]     = useState('');
  const [lnUrls, setLnUrls]     = useState(['']);

  const isLnCustom  = lnType === ADD_NEW_VALUE;
  const lnEffective = isLnCustom ? lnCustom.trim() : lnType;
  const canAddLink  = Boolean(lnEffective && lnUrls.some((u) => u.trim()));

  const resetLink = () => {
    setOpenLink(false); setLnType(DEFAULT_TYPE); setLnCustom('');
    setLnName(''); setLnUrls(['']);
  };

  const handleAddUrl    = () => setLnUrls((prev) => [...prev, '']);
  const handleUrlChange = (i, val) => setLnUrls((prev) => prev.map((u, idx) => idx === i ? val : u));
  const handleUrlRemove = (i) => setLnUrls((prev) => prev.filter((_, idx) => idx !== i));

  const handleAddLink = async () => {
    if (!canAddLink) return;
    const validUrls = lnUrls.map((u) => u.trim()).filter(Boolean);
    const groupName = getGroupForType(lnEffective);
    try {
      const data = await addMediaLinks(asset_model, reference_id, {
        group_name: groupName,
        asset_type: lnEffective,
        name: filenameSafeName(lnName) || undefined,
        urls: validUrls,
      });
      const added = (Array.isArray(data) ? data : [data]).map((a) => mapAssetFromApi(a, lnEffective));
      setGroups((prev) => {
        const next = prev.map((g) => (g.asset_type === lnEffective ? { ...g, assets: [...added, ...g.assets] } : g));
        if (!next.some((g) => g.asset_type === lnEffective)) next.push({ asset_type: lnEffective, assets: added });
        return sortGroupsByAssetType(next);
      });
    } catch (err) {
      console.error('Add link failed:', err);
    }
    resetLink();
  };

  const performDelete = async (id) => {
    try {
      await deleteMedia(id);
      setSelectedIds((prev) => { const n = new Set(prev); n.delete(id); return n; });
      setGroups((prev) =>
        prev
          .map((g) => ({ ...g, assets: g.assets.filter((a) => a.id !== id) }))
          .filter((g) => g.assets.length > 0)
      );
      if (onBrochureChange && groups.some((g) => g.asset_type === 'brochure' && g.assets.some((a) => a.id === id))) onBrochureChange();
    } catch (err) {
      console.error('Delete media failed:', err);
    }
  };

  const handleDeleteClick = (id) => setConfirmDelete({ type: 'single', id });

  const toggleSelection = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const performBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setBulkDeleting(true);
    try {
      await deleteMediaBulk(ids);
      const hadBrochure = groups.some((g) => (g.asset_type || '').toLowerCase() === 'brochure' && g.assets.some((a) => ids.includes(a.id)));
      setSelectedIds(new Set());
      setGroups((prev) =>
        prev
          .map((g) => ({ ...g, assets: g.assets.filter((a) => !ids.includes(a.id)) }))
          .filter((g) => g.assets.length > 0)
      );
      if (hadBrochure && onBrochureChange) onBrochureChange();
    } catch (err) {
      console.error('Bulk delete media failed:', err);
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleBulkDeleteClick = () => setConfirmDelete({ type: 'bulk', count: selectedIds.size });
  const handleConfirmDelete = async () => {
    if (confirmDelete?.type === 'single' && confirmDelete.id) {
      await performDelete(confirmDelete.id);
    }
    if (confirmDelete?.type === 'bulk') {
      await performBulkDelete();
    }
    setConfirmDelete(null);
  };

  const totalCount = groups.reduce((n, g) => n + g.assets.length, 0);
  const isEmpty = totalCount === 0;

  if (mediaLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress size={28} sx={{ color: T.mint }} />
      </Box>
    );
  }

  const btnSx = (variant) => variant === 'primary' ? {
    textTransform: 'none', fontWeight: 600, fontSize: '13px',
    background: T.mint, color: '#fff', borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0,196,109,.28)',
    '&:hover': { background: '#00b362', boxShadow: '0 4px 14px rgba(0,196,109,.35)' },
  } : {
    textTransform: 'none', fontWeight: 600, fontSize: '13px',
    borderRadius: '8px', borderColor: T.border2, color: T.ink2,
    '&:hover': { borderColor: T.mint, background: T.mintSoft, color: T.g600 },
  };

  const ActionButtons = ({ small }) => (
    <Box sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
      <Button
        variant="contained"
        size={small ? 'small' : 'medium'}
        startIcon={<UploadFileIcon sx={{ fontSize: small ? '15px !important' : '17px !important' }} />}
        onClick={() => setOpenUpload(true)}
        sx={{ ...btnSx('primary'), px: small ? 1.5 : 2.5, py: small ? 0.6 : 1 }}
      >
        Upload file
      </Button>
      <Button
        variant="outlined"
        size={small ? 'small' : 'medium'}
        startIcon={<LinkIcon sx={{ fontSize: small ? '15px !important' : '17px !important' }} />}
        onClick={() => setOpenLink(true)}
        sx={{ ...btnSx('secondary'), px: small ? 1.5 : 2.5, py: small ? 0.6 : 1 }}
      >
        Add link
      </Button>
    </Box>
  );

  const dialogBtnSx = {
    textTransform: 'none', fontWeight: 600, fontSize: '13px',
    background: T.mint, borderRadius: '8px',
    '&:hover': { background: '#00b362' },
    '&.Mui-disabled': { background: T.border2 },
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%', minHeight: 0 }}>

      {isEmpty ? (
        // ── Empty state: centered ──────────────────────────────────────────
        <Box sx={{
          flex: 1, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          gap: '16px', py: 6, px: 3, textAlign: 'center',
        }}>
          <Box sx={{
            width: 52, height: 52, borderRadius: '14px',
            background: T.mintSoft, border: '1.5px solid ' + T.mintBorder,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <InsertDriveFileOutlinedIcon sx={{ fontSize: 26, color: T.mint }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: '14px', fontWeight: 700, color: T.ink, mb: '4px' }}>
              No files or links yet
            </Typography>
            <Typography sx={{ fontSize: '12px', color: T.subtle, maxWidth: 300 }}>
              Brochures, floor plans, legal docs, images, videos or any shareable link.
            </Typography>
          </Box>
          <ActionButtons small={false} />
        </Box>

      ) : (
        // ── Filled state: toolbar top-right + grid ─────────────────────────
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
          <Box sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            px: '16px', py: '10px', borderBottom: '1px solid ' + T.border,
            background: T.white, flexShrink: 0, gap: '8px', flexWrap: 'wrap',
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontSize: '12px', color: T.subtle, fontWeight: 500 }}>
                {totalCount} item{totalCount !== 1 ? 's' : ''}
              </Typography>
              {selectedIds.size > 0 && (
                <>
                  <Typography sx={{ fontSize: '12px', color: T.mint, fontWeight: 600 }}>
                    {selectedIds.size} selected
                  </Typography>
                  <Button
                    size="small"
                    onClick={() => setSelectedIds(new Set())}
                    sx={{ textTransform: 'none', fontSize: '12px', color: T.muted }}
                  >
                    Clear
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={handleBulkDeleteClick}
                    disabled={bulkDeleting}
                    sx={{
                      textTransform: 'none', fontSize: '12px', fontWeight: 600,
                      background: '#ef4444', '&:hover': { background: '#dc2626' },
                    }}
                  >
                    {bulkDeleting ? 'Deleting…' : `Delete selected (${selectedIds.size})`}
                  </Button>
                </>
              )}
            </Box>
            <ActionButtons small={true} />
          </Box>

          <Box sx={{ flex: 1, overflowY: 'auto', p: '16px', display: 'flex', flexDirection: 'column', gap: 3 }}>
            {sortGroupsByAssetType(groups).map(({ asset_type, assets }) => (
              <Box key={asset_type || 'other'}>
                <Typography
                  component="h3"
                  sx={{
                    fontSize: '11px',
                    fontWeight: 700,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    color: T.muted,
                    mb: 1.5,
                    pb: 0.5,
                    borderBottom: '1px solid',
                    borderColor: T.border,
                  }}
                >
                  {getLabelForType(asset_type)}
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                    gap: '8px',
                  }}
                >
                  {assets.map((item) => (
                    <MediaCard
                      key={item.id}
                      item={item}
                      selected={selectedIds.has(item.id)}
                      onToggleSelect={toggleSelection}
                      onDelete={handleDeleteClick}
                      onReplace={isProjectLevel && projectName
                        && SINGLE_FILE_TYPES.includes((item.asset_type || '').toLowerCase())
                        ? () => {
                            setReplacingBrochureId(item.id);
                            setOpenUpload(true);
                            setUpType((item.asset_type || '').toLowerCase());
                            setUpName(filenameSafeName(projectName) || '');
                            setUpFiles([]);
                          }
                        : undefined}
                    />
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* Upload file dialog */}
      <Dialog open={openUpload} onClose={resetUpload} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: '12px' } }}>
        <DialogTitle sx={{ fontSize: '15px', fontWeight: 700, color: T.ink, pb: 1 }}>
          Upload file
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '14px', pt: 0.5 }}>
            <AssetTypeSelect 
              value={upType} 
              onChange={setUpType}
              groups={groups}
              disabledTypes={sortGroupsByAssetType(groups)
                .filter((g) => {
                  const assetType = (g.asset_type || '').toLowerCase();
                  // Disable if single file type already exists
                  if (SINGLE_FILE_TYPES.includes(assetType) && g.assets.length > 0) {
                    return true;
                  }
                  // Disable image if max count reached
                  if (assetType === 'image' && currentImageCount >= MAX_IMAGE_COUNT) {
                    return true;
                  }
                  // Disable video if max count reached
                  if (assetType === 'video' && currentVideoCount >= MAX_VIDEO_COUNT) {
                    return true;
                  }
                  return false;
                })
                .map((g) => (g.asset_type || '').toLowerCase())}
            />
            {isUpCustom && (
              <TextField size="small" fullWidth label="Custom type label" placeholder="e.g. Possession Letter"
                value={upCustom} onChange={(e) => setUpCustom(e.target.value)}
                sx={{ '& .MuiInputBase-input': { fontSize: '13px' } }} />
            )}
            <TextField size="small" fullWidth label="Name (optional)"
              placeholder="e.g. Tower_A_Floor_Plan_v2"
              value={upName} onChange={(e) => setUpName(e.target.value.replace(/\s+/g, '_'))}
              sx={{ '& .MuiInputBase-input': { fontSize: '13px' } }} />

            <Box
              onClick={() => canAddMore && inputRef.current?.click()}
              onDrop={handleDrop} onDragOver={handleDragOver} onDragLeave={handleDragLeave}
              sx={{
                border: '1.5px dashed',
                borderColor: dragOver ? T.mint : T.border2,
                borderRadius: '10px',
                background: dragOver ? T.mintSoft : T.surface,
                py: 2, px: 2, textAlign: 'center',
                cursor: canAddMore ? 'pointer' : 'default',
                transition: 'all 150ms ease',
              }}
            >
              <input
                ref={inputRef}
                type="file"
                hidden
                multiple={!isSingleFileUpload}
                accept={isBrochureUpload ? 'application/pdf,.pdf' : isImageUpload ? 'image/*' : isVideoUpload ? 'video/*' : undefined}
                onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
              />
              {upFiles.length > 0 ? (
                <>
                  <Typography sx={{ fontSize: '12px', color: T.subtle, mb: 1 }}>
                    {isSingleFileUpload
                      ? `${upFiles.length} file selected`
                      : isImageUpload
                        ? `${upFiles.length} of ${remainingImageSlots} image${remainingImageSlots !== 1 ? 's' : ''} selected (${currentImageCount} already uploaded, max ${MAX_IMAGE_COUNT})`
                        : isVideoUpload
                          ? `${upFiles.length} of ${remainingVideoSlots} video${remainingVideoSlots !== 1 ? 's' : ''} selected (${currentVideoCount} already uploaded, max ${MAX_VIDEO_COUNT})`
                          : `${upFiles.length} of ${MAX_UPLOAD_FILES} file${MAX_UPLOAD_FILES !== 1 ? 's' : ''} selected`}
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, maxHeight: 160, overflowY: 'auto' }}>
                    {upFiles.map((file, i) => (
                      <Box
                        key={i}
                        sx={{
                          display: 'flex', alignItems: 'center', gap: 1,
                          py: 0.75, px: 1.25, borderRadius: 1, bgcolor: 'grey.50',
                          border: '1px solid', borderColor: 'grey.200',
                        }}
                      >
                        <InsertDriveFileOutlinedIcon sx={{ fontSize: 18, color: T.subtle, flexShrink: 0 }} />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{ fontSize: '12px', fontWeight: 500, color: T.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {file.name}
                          </Typography>
                          <Typography sx={{ fontSize: '10px', color: T.subtle }}>
                            {(file.size / 1024 / 1024).toFixed(2)} MB
                          </Typography>
                        </Box>
                        <IconButton size="small" onClick={(ev) => { ev.stopPropagation(); removeFileAt(i); }} sx={{ color: T.subtle, '&:hover': { color: '#ef4444' } }}>
                          <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                  {canAddMore && !isSingleFileUpload && (
                    <Typography sx={{ fontSize: '11px', color: T.subtle, mt: 1 }}>
                      {isImageUpload
                        ? `Click or drop to add more (${remainingImageSlots - upFiles.length} remaining, max ${MAX_IMAGE_SIZE_MB}MB each)`
                        : isVideoUpload
                          ? `Click or drop to add more (${remainingVideoSlots - upFiles.length} remaining, max ${MAX_VIDEO_SIZE_MB}MB each)`
                          : `Click or drop to add more (max ${MAX_UPLOAD_FILES})`}
                    </Typography>
                  )}
                  {!canAddMore && (isImageUpload || isVideoUpload) && (
                    <Typography sx={{ fontSize: '11px', color: '#ef4444', mt: 1, fontWeight: 600 }}>
                      {isImageUpload
                        ? `Maximum ${MAX_IMAGE_COUNT} images reached`
                        : `Maximum ${MAX_VIDEO_COUNT} videos reached`}
                    </Typography>
                  )}
                </>
              ) : (
                <>
                  <Box sx={{
                    width: 38, height: 38, borderRadius: '10px', background: T.surface2,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: '10px',
                  }}>
                    <UploadFileIcon sx={{ fontSize: 20, color: T.subtle }} />
                  </Box>
                  <Typography sx={{ fontSize: '13px', fontWeight: 600, color: T.ink }}>
                    {isBrochureUpload
                      ? 'Drag & drop or click to choose (1 PDF file)'
                      : isImageUpload
                        ? `Drag & drop or click to choose (up to ${remainingImageSlots} image${remainingImageSlots !== 1 ? 's' : ''})`
                        : isVideoUpload
                          ? `Drag & drop or click to choose (up to ${remainingVideoSlots} video${remainingVideoSlots !== 1 ? 's' : ''})`
                          : isSingleFileUpload
                            ? 'Drag & drop or click to choose (1 file)'
                            : `Drag & drop or click to choose (up to ${MAX_UPLOAD_FILES} files)`}
                  </Typography>
                  <Typography sx={{ fontSize: '11px', color: T.subtle, mt: '3px' }}>
                    {isBrochureUpload
                      ? 'PDF only'
                      : isImageUpload
                        ? `Max ${MAX_IMAGE_SIZE_MB}MB per image, ${remainingImageSlots} of ${MAX_IMAGE_COUNT} slots available`
                        : isVideoUpload
                          ? `Max ${MAX_VIDEO_SIZE_MB}MB per video, ${remainingVideoSlots} of ${MAX_VIDEO_COUNT} slots available`
                          : 'PDF, images, videos, spreadsheets — any format'}
                  </Typography>
                </>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: '8px' }}>
          <Button onClick={resetUpload} disabled={uploading} sx={{ textTransform: 'none', color: T.muted, fontSize: '13px' }}>Cancel</Button>
          <Button variant="contained" onClick={handleUpload} disabled={!canUpload || uploading} sx={dialogBtnSx}>
            {uploading ? `Uploading…` : isBrochureUpload ? `Upload file` : `Upload ${upFiles.length} file${upFiles.length !== 1 ? 's' : ''}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add via link dialog — multi-URL */}
      <Dialog open={openLink} onClose={resetLink} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: '12px' } }}>
        <DialogTitle sx={{ fontSize: '15px', fontWeight: 700, color: T.ink, pb: 1 }}>
          Add shareable links
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '14px', pt: 0.5 }}>
            <AssetTypeSelect value={lnType} onChange={setLnType} groups={groups} />
            {isLnCustom && (
              <TextField size="small" fullWidth label="Custom type label" placeholder="e.g. Possession Letter"
                value={lnCustom} onChange={(e) => setLnCustom(e.target.value)}
                sx={{ '& .MuiInputBase-input': { fontSize: '13px' } }} />
            )}
            <TextField size="small" fullWidth label="Name (optional)"
              placeholder="e.g. Phase_2_Brochure_Google_Drive"
              value={lnName} onChange={(e) => setLnName(e.target.value.replace(/\s+/g, '_'))}
              sx={{ '& .MuiInputBase-input': { fontSize: '13px' } }} />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {lnUrls.map((url, i) => (
                <UrlRow
                  key={i} index={i} value={url}
                  onChange={(val) => handleUrlChange(i, val)}
                  onRemove={() => handleUrlRemove(i)}
                  showRemove={lnUrls.length > 1}
                />
              ))}
              <Button
                size="small" startIcon={<AddIcon />} onClick={handleAddUrl}
                sx={{
                  alignSelf: 'flex-start', textTransform: 'none',
                  fontSize: '12px', color: T.mint, fontWeight: 600,
                  '&:hover': { background: T.mintSoft },
                }}
              >
                Add another URL
              </Button>
            </Box>

            <Typography sx={{ fontSize: '11px', color: T.subtle }}>
              Google Drive, Dropbox, YouTube, any shareable link — paste as-is.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: '8px' }}>
          <Button onClick={resetLink} sx={{ textTransform: 'none', color: T.muted, fontSize: '13px' }}>Cancel</Button>
          <Button variant="contained" onClick={handleAddLink} disabled={!canAddLink} sx={dialogBtnSx}>
            Save links
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog
        open={Boolean(confirmDelete)}
        onClose={() => !bulkDeleting && setConfirmDelete(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px' } }}
      >
        <DialogTitle sx={{ fontSize: '15px', fontWeight: 700, color: T.ink }}>
          Delete {confirmDelete?.type === 'bulk' ? `${confirmDelete.count} item${confirmDelete.count !== 1 ? 's' : ''}` : 'this item'}?
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '13px', color: T.muted }}>
            {confirmDelete?.type === 'bulk'
              ? 'This action cannot be undone.'
              : 'This item will be permanently removed.'}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: '8px' }}>
          <Button onClick={() => setConfirmDelete(null)} disabled={bulkDeleting} sx={{ textTransform: 'none', color: T.muted, fontSize: '13px' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
            disabled={bulkDeleting}
            sx={{ textTransform: 'none', fontWeight: 600, fontSize: '13px' }}
          >
            {bulkDeleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MediaAndDocuments;
